export type DocumentStatus =
  'loaded' | 'processing' | 'completed' | 'failed' | 'unknown';

const LOADED_STATUSES = new Set(['loaded', 'uploaded']);
const PROCESSING_STATUSES = new Set(['processing']);
const COMPLETED_STATUSES = new Set(['completed', 'processed']);
const FAILED_STATUSES = new Set(['failed']);

export const normalizeDocumentStatus = (status: string): DocumentStatus => {
  const normalized = status.toLowerCase();

  if (LOADED_STATUSES.has(normalized)) {
    return 'loaded';
  }

  if (PROCESSING_STATUSES.has(normalized)) {
    return 'processing';
  }

  if (COMPLETED_STATUSES.has(normalized)) {
    return 'completed';
  }

  if (FAILED_STATUSES.has(normalized)) {
    return 'failed';
  }

  return 'unknown';
};

export const isDocumentLoaded = (status: string): boolean =>
  normalizeDocumentStatus(status) === 'loaded';

export const isDocumentProcessing = (status: string): boolean =>
  normalizeDocumentStatus(status) === 'processing';

export const isDocumentCompleted = (status: string): boolean =>
  normalizeDocumentStatus(status) === 'completed';

export const isDocumentFailed = (status: string): boolean =>
  normalizeDocumentStatus(status) === 'failed';

export const isDocumentUnknown = (status: string): boolean =>
  normalizeDocumentStatus(status) === 'unknown';

export const isDocumentTerminal = (status: string): boolean =>
  isDocumentCompleted(status) ||
  isDocumentFailed(status) ||
  isDocumentUnknown(status);

export const isDocumentInProgress = (status: string): boolean =>
  !isDocumentTerminal(status);

export const formatDocumentStatusLabel = (status: string): string => {
  const normalized = normalizeDocumentStatus(status);
  if (normalized === 'unknown') {
    return 'Unknown';
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const API_ROLE_LABELS: Record<string, string> = {
  CUSTOMER_CARE: 'Customer Support',
  ENG: 'Engineering',
  LOCATION_ADMIN: 'Facility Admin',
  LOCATION_SCHEDULER: 'Scheduler',
  FACILITY_ADMIN: 'Facility Admin',
  FACILITY_SCHEDULER: 'Scheduler',
};

export const formatAllowedRolesLabel = (roles?: string[]): string => {
  if (!roles?.length) {
    return '—';
  }

  return roles.map((role) => API_ROLE_LABELS[role] ?? role).join(', ');
};

export const normalizeAllowedRoles = (
  value: unknown,
  filename?: string,
): string[] | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (Array.isArray(value)) {
    const roles = value.filter(
      (role): role is string =>
        typeof role === 'string' && role.trim().length > 0,
    );
    return roles.length > 0 ? roles : undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }

    try {
      return normalizeAllowedRoles(JSON.parse(trimmed), filename);
    } catch {
      return undefined;
    }
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;

    if (filename) {
      const normalizedFilename = filename.trim().toLowerCase();
      const matchedEntry = Object.entries(record).find(
        ([key]) => key.trim().toLowerCase() === normalizedFilename,
      );

      if (matchedEntry) {
        return normalizeAllowedRoles(matchedEntry[1], filename);
      }
    }

    const roles = [
      ...new Set(
        Object.values(record).flatMap(
          (entry) => normalizeAllowedRoles(entry, filename) ?? [],
        ),
      ),
    ];

    return roles.length > 0 ? roles : undefined;
  }

  return undefined;
};
